import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2, Quote } from "lucide-react";
import { fetchTestimonials, createTestimonial, updateTestimonial, deleteTestimonial } from "@/lib/api";
import { formatApiErrorDetail } from "@/contexts/AuthContext";

const empty = { quote: "", author: "", role: "", organization: "", avatar_url: "" };

export default function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try { setItems(await fetchTestimonials()); } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await deleteTestimonial(id);
      toast.success("Testimonial deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-end justify-between gap-4 mb-7">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">Testimonials</h1>
          <p className="text-slate-500 mt-1 text-sm">Quotes from partners displayed in the testimonial slider.</p>
        </div>
        <button onClick={() => setEditing(empty)} data-testid="testimonials-add" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5 py-2.5 text-sm font-medium shadow-md shadow-blue-600/20 shrink-0">
          <Plus size={16} /> Add Testimonial
        </button>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-32 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <p className="text-slate-500 py-10 text-center">No testimonials yet.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {items.map((t) => (
            <article key={t.id} data-testid={`admin-testimonial-${t.id}`} className="bg-white border border-slate-200 rounded-2xl p-6">
              <Quote className="text-blue-100" size={32} />
              <p className="text-sm text-slate-800 mt-2 leading-relaxed line-clamp-4">"{t.quote}"</p>
              <div className="mt-5 flex items-center gap-3">
                {t.avatar_url && <img src={t.avatar_url} alt={t.author} className="w-10 h-10 rounded-full object-cover" />}
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 text-sm">{t.author}</div>
                  <div className="text-xs text-slate-500 truncate">{t.role} · {t.organization}</div>
                </div>
              </div>
              <div className="flex gap-2 mt-5">
                <button data-testid={`testimonial-edit-${t.id}`} onClick={() => setEditing(t)} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-blue-600 px-3 py-1.5 rounded-lg hover:bg-blue-50"><Pencil size={13} /> Edit</button>
                <button data-testid={`testimonial-delete-${t.id}`} onClick={() => handleDelete(t.id)} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50"><Trash2 size={13} /> Delete</button>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && <TestimonialDialog initial={editing} onClose={() => setEditing(null)} onSaved={refresh} />}
    </div>
  );
}

function TestimonialDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({ ...empty, ...initial });
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial.id;
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await updateTestimonial(initial.id, form);
        toast.success("Testimonial updated");
      } else {
        await createTestimonial(form);
        toast.success("Testimonial created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-testid="testimonial-dialog">
      <form onSubmit={onSave} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="font-heading text-xl font-semibold text-slate-900">{isEdit ? "Edit Testimonial" : "Add Testimonial"}</h2>
          <button type="button" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Quote *</label>
            <textarea name="quote" value={form.quote} onChange={onChange} required rows={4} data-testid="testimonial-input-quote" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Author *" name="author" value={form.author} onChange={onChange} required testid="testimonial-input-author" />
            <Field label="Role *" name="role" value={form.role} onChange={onChange} required testid="testimonial-input-role" placeholder="e.g. CMO" />
          </div>
          <Field label="Organization *" name="organization" value={form.organization} onChange={onChange} required testid="testimonial-input-organization" />
          <Field label="Avatar URL (optional)" name="avatar_url" value={form.avatar_url || ""} onChange={onChange} testid="testimonial-input-avatar" placeholder="https://…" />
        </div>
        <div className="flex justify-end gap-2 p-6 border-t border-slate-100 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button type="submit" disabled={saving} data-testid="testimonial-save" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 text-sm font-medium rounded-lg disabled:opacity-70">
            {saving && <Loader2 size={14} className="animate-spin" />} Save
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, name, value, onChange, required, testid, placeholder, type = "text" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">{label}</label>
      <input type={type} name={name} value={value || ""} onChange={onChange} required={required} placeholder={placeholder} data-testid={testid}
        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
    </div>
  );
}
